// Central in-memory cache with TTL + in-flight dedupe.
// Optional persistence goes through the shared pp-cache IndexedDB (kv store).
// No Firestore usage here.

import { idbGet, idbSet, idbDel } from "./idbKV";

const mem = new Map(); // key -> { value, expiresAt, inFlight: Promise|null }
const now = () => Date.now();

const MAX_PERSIST_BYTES =
  Number(process.env.REACT_APP_CACHE_PERSIST_MAX_BYTES) || 256 * 1024;

/**
 * getOrFetch({
 *   key, ttlMs, fetcher,
 *   persist = false, persistKey = key,
 *   staleWhileRevalidate = false
 * })
 */
export async function getOrFetch({
  key,
  ttlMs,
  fetcher,
  persist = false,
  persistKey = key,
  staleWhileRevalidate = false,
}) {
  if (!key) throw new Error("getOrFetch: key is required");
  if (typeof fetcher !== "function") throw new Error("getOrFetch: fetcher must be a function");

  const entry = mem.get(key);
  const t = now();

  // Fresh in-memory
  if (entry && entry.value !== undefined && entry.expiresAt > t) return entry.value;

  // Share in-flight
  if (entry && entry.inFlight) return entry.inFlight;

  // Try persisted value
  let persisted = null;
  if (persist) {
    try { persisted = await idbGet(persistKey); } catch {}
    const valid = persisted && typeof persisted.expiresAt === "number" && persisted.expiresAt > t;
    if (valid) {
      const v = persisted.value;
      mem.set(key, { value: v, expiresAt: persisted.expiresAt, inFlight: null });
      return v;
    }
  }

  // SWR: serve stale immediately and refresh in background
  if (staleWhileRevalidate && persist && persisted && persisted.value !== undefined) {
    (async () => {
      try {
        const v = await fetcher();
        const expiresAt = now() + Math.max(1, Number(ttlMs) || 0);
        mem.set(key, { value: v, expiresAt, inFlight: null });
        try {
          const asStr = JSON.stringify(v);
          if (asStr.length <= MAX_PERSIST_BYTES) {
            await idbSet(persistKey, { value: v, expiresAt });
          } else {
            await idbDel(persistKey);
          }
        } catch {}
      } catch {}
    })();
    return persisted.value;
  }

  // Fetch fresh, populate memory + (optional) IDB
  const p = (async () => {
    const v = await fetcher();
    const expiresAt = t + Math.max(1, Number(ttlMs) || 0);
    mem.set(key, { value: v, expiresAt, inFlight: null });

    if (persist) {
      try {
        const asStr = JSON.stringify(v);
        if (asStr.length <= MAX_PERSIST_BYTES) {
          await idbSet(persistKey, { value: v, expiresAt });
        } else {
          await idbDel(persistKey);
        }
      } catch {}
    }
    return v;
  })();

  mem.set(key, { value: entry?.value, expiresAt: entry?.expiresAt ?? 0, inFlight: p });
  return p;
}

/**
 * Same as getOrFetch, but returns { value, fromCache }.
 * fromCache=true when served from fresh in-memory or valid persisted cache (IDB/SWR).
 * fromCache=false when fetched from network/DB.
 */
export async function getOrFetchWithMeta(opts) {
  const {
    key,
    ttlMs,
    fetcher,
    persist = false,
    persistKey = key,
    staleWhileRevalidate = false,
  } = opts || {};
  if (!key) throw new Error("getOrFetchWithMeta: key is required");
  if (typeof fetcher !== "function") throw new Error("getOrFetchWithMeta: fetcher must be a function");

  const entry = mem.get(key);
  const t = now();

  // Fresh in-memory
  if (entry && entry.value !== undefined && entry.expiresAt > t) {
    return { value: entry.value, fromCache: true };
  }

  // Share in-flight
  if (entry && entry.inFlight) {
    const v = await entry.inFlight;
    return { value: v, fromCache: false };
  }

  // Try persisted value
  let persisted = null;
  if (persist) {
    try { persisted = await idbGet(persistKey); } catch {}
    const valid = persisted && typeof persisted.expiresAt === "number" && persisted.expiresAt > t;
    if (valid) {
      const v = persisted.value;
      mem.set(key, { value: v, expiresAt: persisted.expiresAt, inFlight: null });
      return { value: v, fromCache: true };
    }
  }

  // SWR path: serve stale now and refresh in background
  if (staleWhileRevalidate && persist && persisted && persisted.value !== undefined) {
    (async () => {
      try {
        const v = await fetcher();
        const expiresAt = now() + Math.max(1, Number(ttlMs) || 0);
        mem.set(key, { value: v, expiresAt, inFlight: null });
        try {
          const asStr = JSON.stringify(v);
          if (asStr.length <= MAX_PERSIST_BYTES) {
            await idbSet(persistKey, { value: v, expiresAt });
          } else {
            await idbDel(persistKey);
          }
        } catch {}
      } catch {}
    })();
    return { value: persisted.value, fromCache: true };
  }

  // Fresh fetch
  const p = (async () => {
    const v = await fetcher();
    const expiresAt = t + Math.max(1, Number(ttlMs) || 0);
    mem.set(key, { value: v, expiresAt, inFlight: null });

    if (persist) {
      try {
        const asStr = JSON.stringify(v);
        if (asStr.length <= MAX_PERSIST_BYTES) {
          await idbSet(persistKey, { value: v, expiresAt });
        } else {
          await idbDel(persistKey);
        }
      } catch {}
    }
    return v;
  })();

  mem.set(key, { value: entry?.value, expiresAt: entry?.expiresAt ?? 0, inFlight: p });
  const v = await p;
  return { value: v, fromCache: false };
}

/** Invalidate one key or all keys with a prefix (mem only) + exact-key in IDB */
export function invalidate(keyOrPrefix) {
  for (const k of Array.from(mem.keys())) {
    if (k === keyOrPrefix || k.startsWith(keyOrPrefix)) mem.delete(k);
  }
  idbDel(keyOrPrefix).catch(() => {}); // best-effort exact-key purge
}

/** Prime a value (memory only) */
export function prime({ key, value, ttlMs = 60_000 }) {
  const expiresAt = now() + Math.max(1, Number(ttlMs) || 0);
  mem.set(key, { value, expiresAt, inFlight: null });
}
